import React, { ReactElement } from 'react';
import { BrowserRouter, Route, Switch, useHistory } from 'react-router-dom';

import { Callback } from './authRoutes/callback';
import { Logout } from './authRoutes/logout';
import { SilentRenew } from './authRoutes/silentRenew';
import HomeRouter from 'router/homeRoute';

import { Loader } from 'ui/patterns';
import { Header,  MainContent } from 'ui/patterns';

import {  retrieveAndParseToken, useAuth } from 'hooks/useAuth';
import { useGetUserPermissions } from 'hooks';

import { AuthServiceSingleton } from 'utils/authenticationHelper/authServices';
import type AuthService from 'utils/authenticationHelper/authServices';
import {
  AUTH_EXPIRY_VISIBLE,
  LOGIN_TYPE,
  LOGIN_TYPES,
} from 'utils/authenticationHelper/authConstant';
import { useRenewTimer } from 'utils/authenticationHelper/renewTimer';
import { otherTabsAvailable } from 'utils/otherTabsAvailability';

export function PrivatePage() {
  useRenewTimer();
  const [mounted, setMounted] = React.useState<boolean>(false);
  const [authCompleted, setAuthCompleted] = React.useState<boolean>(false);
  const history = useHistory();
  const authServiceRef = React.useRef<AuthService>();
  const { userInfo }: any = useAuth();

  const { data: permissions, isLoading: isUserPermissionloading } =
    useGetUserPermissions();

  const userPermissionSet = React.useMemo(() => {
    if (permissions?.Data?.length) {
      const temp = [...permissions.Data];
      const hasQuestionRepoPermission = temp.includes(
        'CanEditQuestionRepository',
      );
      const hasAccountBuilderPermission = temp.includes(
        'CanAccessAccountBuilder',
      );
      const hasEditFormPermission = temp.includes('CanEditForms');
      const hasAccountModifyPermission = temp.includes('CanModifyAllAccounts');
      const hasPurgePermissions = temp.includes('CanPurgeAccount');
      const isTelephonyAdmin = temp.includes('CanModifyTelephonySettings');
      const isRelationshipManager = temp?.includes('RelationshipManagers');
      const canModifiyFtpFingerPrint = temp?.includes(
        'CanModifyFTPFingerPrint',
      );
      const CanManageChoiceQuestionOptions = temp?.includes(
        'CanManageChoiceQuestionOptions',
      );
      const CanEditQuestionAttributes = temp?.includes(
        'CanEditQuestionAttributes',
      );
      const hasSuperAdministratorsPermission = temp?.includes(
        'SuperAdministrators',
      );
      const hasAdminPermission = temp?.includes('Administrators');

      const userPermissions = {
        CanEditQuestionRepository: hasQuestionRepoPermission,
        CanAccessAccountBuilder: hasAccountBuilderPermission,
        CanEditForms: hasEditFormPermission,
        CanModifyAllAccounts: hasAccountModifyPermission,
        canPurgeAccount: hasPurgePermissions,
        isTelephonyAdmin: isTelephonyAdmin,
        isRelationshipManager: isRelationshipManager,
        canModifiyFtpFingerPrint: canModifiyFtpFingerPrint,
        CanManageChoiceQuestionOptions: CanManageChoiceQuestionOptions,
        CanEditQuestionAttributes: CanEditQuestionAttributes,
        isAdmin: hasAdminPermission,
        isSuperAdmin: hasSuperAdministratorsPermission,
      };
      localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
      return { ...userPermissions };
    }
  }, [permissions?.Data]);

  React.useEffect(() => {
    const authCondition = async () => {
      if (mounted) {
        const auth = retrieveAndParseToken(false);
        const windowsAvailable = await otherTabsAvailable();
        authServiceRef.current = AuthServiceSingleton.getInstance();
        const loginType = localStorage.getItem(LOGIN_TYPE);
        localStorage.removeItem(LOGIN_TYPE);
        sessionStorage.removeItem(AUTH_EXPIRY_VISIBLE);
        if (auth?.access_token && (windowsAvailable || loginType)) {
          setAuthCompleted(true);
        } else {
          localStorage.setItem(LOGIN_TYPE, LOGIN_TYPES.FIRST_LOGIN);
          authServiceRef.current.signIn();
        }
      }
    };
    authCondition();
  }, [mounted, history]);
  React.useEffect(() => {
    setTimeout(() => setMounted(true), 1500);
  }, []);

  if (!authCompleted) return <Loader />;

  const displayName =
    userInfo &&
    userInfo['http://schemas.microsoft.com/identity/claims/displayname'];

  return (
    <>
      <Header username={displayName} permissions={userPermissionSet} />
      {userPermissionSet !== undefined ? (
        <>
          <HomeRouter />
        </>
      ) : (
        <MainContent></MainContent>
      )}

      {isUserPermissionloading && <Loader />}
    </>
  );
}

const RefreshLanding = () => {
  const history = useHistory();
  const authServiceRef = React.useRef<AuthService>();
  React.useEffect(() => {
    authServiceRef.current = AuthServiceSingleton.getInstance();
    localStorage.setItem(LOGIN_TYPE, LOGIN_TYPES.REFRESH_LOGIN);
    authServiceRef.current?.signIn();
  }, [history]);

  return <Loader />;
};

const AppRouter = (): ReactElement => {
  return (
    <BrowserRouter>
      <Switch>
        <Route
          exact={true}
          path="/authentication/callback"
          component={Callback}
        />
        <Route exact={true} path="/logout" component={Logout} />
        <Route
          exact={true}
          path="/authentication/silent_callback"
          component={SilentRenew}
        />
        <Route path="/refreshToken" component={RefreshLanding} />
        <Route path="/" component={PrivatePage} />
      </Switch>
    </BrowserRouter>
  );
};
export default AppRouter;
